import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface Business {
  id: string;
  name: string;
  image: string;
  rating: number;
  reviews: number;
  description: string;
  address: string;
  category: string;
  phone: string;
  premiumType?: 'gold' | 'silver' | 'bronze';
  instagram?: string;
}

interface Category {
  id: string;
  name: string;
  emoji: string;
  description: string;
  enabled: boolean;
}

interface BusinessEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  business: Business;
  onSave: (updatedBusiness: Business) => Promise<void>;
}

const BusinessEditModal: React.FC<BusinessEditModalProps> = ({
  isOpen,
  onClose,
  business,
  onSave,
}) => {
  const [formData, setFormData] = useState<Business>(business);
  const [isSaving, setIsSaving] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://getbusinesscategorylist-b7jrylvg3a-uc.a.run.app');
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    fetchCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Remove @ from beginning of instagram handle if present
      const formDataToSubmit = {
        ...formData,
        instagram: formData.instagram ? formData.instagram.replace(/^@/, '') : ''
      };
      await onSave(formDataToSubmit);
      onClose();
    } catch (error) {
      console.error('Error saving business:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-background rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold text-white">
              Editar Estabelecimento
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Nome
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary h-24"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Endereço
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary h-24"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Telefone
              </label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Instagram
              </label>
              <input
                type="text"
                value={formData.instagram || ''}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary"
                placeholder="@instagram"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Categoria
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary"
              >
                <option value="">Selecione uma categoria</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.emoji} {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Tipo Premium
              </label>
              <select
                value={formData.premiumType || ''}
                onChange={(e) => setFormData({ ...formData, premiumType: e.target.value as 'gold' | 'silver' | 'bronze' | undefined })}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary"
              >
                <option value="">Nenhum</option>
                <option value="gold">Gold</option>
                <option value="silver">Silver</option>
                <option value="bronze">Bronze</option>
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex-1 btn-primary"
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default BusinessEditModal; 